import { createServerFn } from "@tanstack/react-start";

import {
  addWarmupConnection,
  disableWarmup,
  disconnectConnectedAccount,
  enableWarmup,
  listConnectedAccountProviders,
  listConnectedAccounts,
  listWarmupConnections,
  listWarmupProviders,
  listWarmups,
  removeWarmupConnection,
  startConnectedAccountAuthorization,
  type ConnectedAccountGrant,
  type ConnectedAccountProviderId,
  type WarmupProviderId,
} from "./server";
import { mapConnectedAccountsToView } from "./view-model";

function passthroughValidator<T>(value: T) {
  return value;
}

/**
 * One payload for the page: the providers a user may connect, the accounts, the warmup of each
 * account's inbox, the warmup providers an enable may pick and the user's own connections at
 * them. The warmup reads tolerate an unconfigured warmup (the page still lists the accounts).
 */
export const getConnectedAccountsView = createServerFn({ method: "GET" }).handler(async () => {
  const [providers, accounts, warmups, warmupProviders, warmupConnections] = await Promise.all([
    listConnectedAccountProviders(),
    listConnectedAccounts(),
    listWarmups().catch(() => []),
    listWarmupProviders().catch(() => []),
    listWarmupConnections().catch(() => []),
  ]);

  return mapConnectedAccountsToView({
    providers,
    accounts,
    warmups,
    warmupProviders,
    warmupConnections,
  });
});

export const addWarmupConnectionAction = createServerFn({ method: "POST" })
  .validator(
    passthroughValidator<{
      providerId: Exclude<WarmupProviderId, "bavimail">;
      credentials: Record<string, string>;
    }>,
  )
  .handler(async ({ data }) => {
    await addWarmupConnection(data);
  });

export const removeWarmupConnectionAction = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ connectionId: string }>)
  .handler(async ({ data }) => {
    await removeWarmupConnection(data.connectionId);
  });

export const startConnectedAccountAuthorizationAction = createServerFn({ method: "POST" })
  .validator(
    passthroughValidator<{
      provider: ConnectedAccountProviderId;
      loginHint?: string | null;
      grants?: ConnectedAccountGrant[];
    }>,
  )
  .handler(async ({ data }) => {
    const authorization = await startConnectedAccountAuthorization(data);
    return {
      authorizationUrl: authorization.authorization_url,
      expiresAt: authorization.expires_at,
    };
  });

export const disconnectConnectedAccountAction = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ connectedAccountId: string }>)
  .handler(async ({ data }) => {
    await disconnectConnectedAccount(data.connectedAccountId);
  });

export const enableWarmupAction = createServerFn({ method: "POST" })
  .validator(
    passthroughValidator<{
      inboxId: string;
      providerId: WarmupProviderId;
      dailyVolume?: number | null;
    }>,
  )
  .handler(async ({ data }) => {
    const result = await enableWarmup(data);
    return result.kind === "enabled"
      ? { kind: "enabled" as const }
      : { kind: "authorization_required" as const, message: result.message };
  });

export const disableWarmupAction = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ warmupId: string }>)
  .handler(async ({ data }) => {
    await disableWarmup(data.warmupId);
  });
